import React, { useState, useEffect, useCallback } from "react"
import { Popover, Drawer, rem } from "@mantine/core"
import { Target } from "@/components/TopNavigation/components/MultiPacSelector/Target/Target"
import { Dropdown } from "@/components/TopNavigation/components/MultiPacSelector/Dropdown/Dropdown"

import classes from "@/components/TopNavigation/components/MultiPacSelector/MultiPacSelector.module.css"
import { saveNewSelectedPac, useAvailablePacs } from "@/components/TopNavigation/components/MultiPacSelector/services"
import { useMutation } from "@tanstack/react-query"
import { MultiPacSelectorContext } from "@/components/TopNavigation/components/MultiPacSelector/MultiPacSelectorContext"
import { DRAWER_SIZE } from "@/components/TopNavigation/components/MultiPacSelector/consts"
import { closePacTabs, useCloseListener } from "@/components/TopNavigation/components/MultiPacSelector/helpers"
import { PacOption } from "@/components/TopNavigation/components/MultiPacSelector/types"

type Props = {
    loggedInAsUser?: boolean
    isMobile?: boolean
    popdownIsOpen?: boolean
}

export const MultiPacSelector = ({ loggedInAsUser = false, isMobile = false, popdownIsOpen = false }: Props) => {
    const [search, setSearch] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const [selectedPacLabel, setSelectedPacLabel] = useState("No PAC selected")
    useCloseListener(closePacTabs)

    const onNewPacSelected = useCallback(({ current_pac }: { current_pac: PacOption }) => {
        setIsOpen(false)
        closePacTabs()
        setSelectedPacLabel(current_pac.acronym)
        window.location.reload()
    }, [])

    const availablePacs = useAvailablePacs()
    const availablePacsData = availablePacs?.data

    const selectNewPacMutation = useMutation({
        mutationFn: saveNewSelectedPac,
        onSuccess: onNewPacSelected,
    })
    const selectNewPac = async (newPac: string) => selectNewPacMutation.mutate(newPac)

    const isLoading = availablePacs?.isFetching || selectNewPacMutation.isLoading
    const options = availablePacsData?.options
    const selectedPac = availablePacsData?.selectedPac

    useEffect(() => {
        const selectedOption = options?.find((option) => option.id === selectedPac)
        setSelectedPacLabel(selectedOption?.acronym || "")
        setSearch("")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPac])

    const toggleIsOpen = () => setIsOpen((wasOpen) => !wasOpen)

    if (!options || options.length <= 1) {
        return null
    }

    return (
        <MultiPacSelectorContext.Provider
            value={{
                selectedPac,
                selectNewPac,
                options,
                selectedPacLabel,
                search,
                setSearch,
                isLoading,
            }}
        >
            {!isMobile && (
                <Popover opened={isOpen} onChange={setIsOpen} position="bottom-start" width={rem(205)}>
                    <Popover.Target>
                        <Target
                            isOpen={isOpen}
                            onClick={toggleIsOpen}
                            loggedInAsUser={loggedInAsUser}
                            popdownIsOpen={popdownIsOpen}
                        />
                    </Popover.Target>

                    <Popover.Dropdown className={classes.dropdownWrapper}>
                        <Dropdown />
                    </Popover.Dropdown>
                </Popover>
            )}

            {isMobile && (
                <>
                    <Target
                        isOpen={isOpen}
                        onClick={toggleIsOpen}
                        loggedInAsUser={loggedInAsUser}
                        popdownIsOpen={popdownIsOpen}
                        isMobile
                    />
                    <Drawer
                        title="Change PAC"
                        size={DRAWER_SIZE}
                        opened={isOpen}
                        onClose={toggleIsOpen}
                        position="bottom"
                        classNames={{
                            header: classes.drawerHeader,
                            title: classes.drawerTitle,
                            content: classes.drawerContent,
                        }}
                    >
                        <Dropdown isMobile />
                    </Drawer>
                </>
            )}
        </MultiPacSelectorContext.Provider>
    )
}
